import { useState } from 'react';
import { TextField, Button, Box, Alert, Link as MuiLink } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { AuthLayout } from '@/components/layout';
import { authApi } from '@/api';

export function ActivationCompletePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const initialEmail = (location.state as { email?: string })?.email || '';

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError(t('activation.passwordMismatch'));
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError(t('activation.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      await authApi.completeActivation({ email, code, password });
      enqueueSnackbar(t('activation.activationSuccess'), { variant: 'success' });
      navigate('/login');
    } catch (err) {
      const message = err instanceof Error ? err.message : t('activation.activationError');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t('activation.completeTitle')}
      subtitle={t('activation.completeDescription')}
    >
      <Box component="form" onSubmit={handleSubmit} noValidate>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label={t('auth.email')}
          name="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading || !!initialEmail}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          id="code"
          label={t('activation.code')}
          name="code"
          placeholder={t('activation.codePlaceholder')}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={loading}
          inputProps={{ maxLength: 8 }}
          autoFocus
        />

        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label={t('activation.newPassword')}
          type="password"
          id="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          name="confirmPassword"
          label={t('activation.confirmPassword')}
          type="password"
          id="confirmPassword"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={loading || !email || !code || !password || !confirmPassword}
        >
          {loading ? t('common.loading') : t('activation.activate')}
        </Button>

        <Box sx={{ textAlign: 'center' }}>
          <MuiLink component={Link} to="/activation" variant="body2">
            {t('common.back')}
          </MuiLink>
        </Box>
      </Box>
    </AuthLayout>
  );
}
